import { useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import PlayerContainer from '@App/components/container/PlayerContainer';
import VersionsPanelContainer from '@App/components/container/VersionsPanelContainer';
import InformationsPanelContainer from '@App/components/container/InformationsPanelContainer';
import DownloadPanelContainer from '@App/components/container/DownloadPanelContainer';
import SimultaneousLoginsSpottedPanelContainer from '@App/components/container/SimultaneousLoginsSpottedPanelContainer';
import SelectBriefPanelContainer from '@App/components/container/SelectBriefPanelContainer';
import SelectProjectPanelContainer from '@App/components/container/SelectProjectPanelContainer';
import BackdropContainer from '@App/components/container/BackdropContainer';
import TopBar from '@App/components/layout/TopBar';
import SearchBar from '@App/components/search/SearchBar';
import SoundEffectSearchBar from '@App/components/search/SoundEffectSearchBar';
import Footer from '@App/components/layout/Footer';
import { useDispatch } from 'react-redux';
import { closeSelectProjectPanel } from '@App/store/actions/project';

/** Which search bar the shared header carries. */
export const SEARCH_BAR_MUSIC = 'music';
export const SEARCH_BAR_SOUND_EFFECTS = 'soundEffects';
export const SEARCH_BAR_NONE = false;

export default function ContentPage({ children, allowedToDownload, searchBar }) {
    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(closeSelectProjectPanel());
    }, [dispatch]);

    return (
        <div className="content-page">
            <div className={classnames('fixed-header', { 'fixed-header--without-searchbar': !searchBar })}>
                <TopBar/>
                {searchBar === SEARCH_BAR_SOUND_EFFECTS ? <SoundEffectSearchBar/> : null}
                {searchBar === SEARCH_BAR_MUSIC ? <SearchBar/> : null}
            </div>
            <main className="main">
                {children}
            </main>
            <Footer />
            <PlayerContainer />
            <VersionsPanelContainer />
            <InformationsPanelContainer />
            <DownloadPanelContainer allowedToDownload={allowedToDownload} />
            <SelectBriefPanelContainer />
            <SelectProjectPanelContainer />
            <SimultaneousLoginsSpottedPanelContainer />
            <BackdropContainer />
        </div>
    );
}

ContentPage.propTypes = {
    children: PropTypes.node.isRequired,
    /**
     * On some content pages, even a user without "free" download permissions should be allowed to download,
     * for instance the purchased tracks from their order page.
     *
     * LATER: we should rather get a list of purchased references allowed to be downloaded for the user.
     */
    allowedToDownload: PropTypes.bool,
    /**
     * Which search bar the shared header carries. The music one, with its result dropdown, is the
     * default; /effets-sonores swaps it for the sound effects one (searching the PSE catalogue instead of
     * the music one) rather than rendering a second box in the page, so that the bar stays put when
     * the page scrolls and never paints over the header's own dropdowns. `false` for no bar at all.
     */
    searchBar: PropTypes.oneOf([SEARCH_BAR_MUSIC, SEARCH_BAR_SOUND_EFFECTS, SEARCH_BAR_NONE]),
};

ContentPage.defaultProps = {
    allowedToDownload: false,
    searchBar: SEARCH_BAR_MUSIC,
};
